import { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { observer } from 'mobx-react';
import { flowResult } from 'mobx';
import moment from 'moment-timezone';
import { useStore } from '../../core/utils/hook';
import Breadcrumb from '../Profile/components/Breadcrumb';
import { CalendarCustom, ReactNotifications } from '../../components';
import SelectPageSize from '../../components/Table/SelectPageSize';
import ReactPaging from '../../components/Table/ReactPaging';
import PagingInfo from '../../components/Table/PagingInfo';
import SendMessageScreen from '../GroupAdmin/SendMessageScreen';
import { SYSTEM_PATH, SHARE_KNOWLEDGE_VIEW_STATUS, SHARE_KNOWLEDGE_VIEW_STATUS_MAPPER } from '../../core/configs/constants';
import { handleDownloadFile } from '../../core/utils/file';
import { IconSendMessage, IconExport } from './components/Icon';
import './style.scss';

const formatViewedDate = (date) => {
    return date ? moment(date).tz('Asia/Tokyo').format('YYYY/MM/DD') : '--';
};

const ShareKnowledgeViewerListScreen = observer(() => {
    const { fileId } = useParams();

    // store
    const {
        shareKnowledgeStore: { getFileViewers, lstViewer },
        apiStore: { showLoading, hideLoading },
        userStore: { userInfoHeader }
    } = useStore();

    // states
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [filterName, setFilterName] = useState('');
    const [filterViewStatus, setFilterViewStatus] = useState(''); // '' (All), '0' (未視聴), '1' (視聴中), '2' (視聴完了)
    const [filterFromDate, setFilterFromDate] = useState('');
    const [filterToDate, setFilterToDate] = useState('');
    const [selectedStudent, setSelectedStudent] = useState([]);

    // Temporary form states
    const [searchName, setSearchName] = useState('');
    const [searchViewStatus, setSearchViewStatus] = useState('');
    const [searchFromDate, setSearchFromDate] = useState('');
    const [searchToDate, setSearchToDate] = useState('');
    const [searchRanges, setSearchRanges] = useState([
        {
            startDate: null,
            endDate: new Date(''),
            key: 'selection'
        }
    ]);

    const fetchViewerList = async (pageNumber = 1, limitNumber = 10) => {
        const params = {
            page: pageNumber,
            limit: limitNumber,
            name: filterName,
            viewStatus: filterViewStatus,
            fromDate: filterFromDate,
            toDate: filterToDate
        };
        await flowResult(getFileViewers(fileId, params));
    };

    useEffect(() => {
        fetchViewerList(page, pageSize);
    }, [fileId, page, pageSize, filterName, filterViewStatus, filterFromDate, filterToDate]);

    // Checkbox handlers
    const elements = lstViewer?.elements || [];
    const isAllSelected = elements.length > 0 && elements.every(el => selectedStudent.some(s => s.id === el.id));

    const handleSelectAll = (checked) => {
        if (checked) {
            const newSelection = [...selectedStudent];
            elements.forEach(el => {
                if (!newSelection.some(s => s.id === el.id)) {
                    newSelection.push({
                        id: el.id,
                        firstName: el.firstName,
                        lastName: el.lastName,
                        email: el.email
                    });
                }
            });
            setSelectedStudent(newSelection);
        } else {
            const filtered = selectedStudent.filter(s => !elements.some(el => el.id === s.id));
            setSelectedStudent(filtered);
        }
    };

    const handleRowSelect = (el, checked) => {
        if (checked) {
            setSelectedStudent(prev => {
                if (prev.some(s => s.id === el.id)) return prev;
                return [
                    ...prev,
                    {
                        id: el.id,
                        firstName: el.firstName,
                        lastName: el.lastName,
                        email: el.email
                    }
                ];
            });
        } else {
            setSelectedStudent(prev => prev.filter(s => s.id !== el.id));
        }
    };

    const selectedRowIds = useMemo(() => {
        const ids = {};
        selectedStudent.forEach(s => {
            ids[s.id] = true;
        });
        return ids;
    }, [selectedStudent]);

    const initialTableState = useMemo(() => {
        const pagination = lstViewer?.paginate || {};
        return {
            page: pagination.page || 1,
            size: pagination.size || 10,
            totalRecord: pagination.totalRecord || 0,
            totalPage: pagination.totalPage || 0
        };
    }, [lstViewer?.paginate]);

    // Filter submit
    const handleSearch = (e) => {
        e.preventDefault();
        setFilterName(searchName);
        setFilterViewStatus(searchViewStatus);
        setFilterFromDate(searchFromDate);
        setFilterToDate(searchToDate);
        setPage(1);
    };

    const handleOpenSendMessageModal = () => {
        if (selectedStudent.length === 0) {
            ReactNotifications('warning', '受講者を選択してください。');
            return;
        }
        window.$('#sendMessageScreenModal').modal('show');
    };

    const handleAfterDownload = (msg) => {
        if (msg) {
            ReactNotifications('danger', msg);
        }
        hideLoading();
    };

    const handleExportCSV = () => {
        showLoading();
        handleDownloadFile({
            url: `share-knowledge/group/file/${fileId}/viewers/export`,
            method: 'get',
            data: {
                name: filterName,
                viewStatus: filterViewStatus,
                fromDate: filterFromDate,
                toDate: filterToDate
            },
            cb: handleAfterDownload
        });
    };

    const labels = {
        title: '視聴者一覧',
        fileTitle: '動画タイトル',
        viewerName: '姓名',
        status: '視聴ステータス',
        statusAll: '全て',
        statusUnviewed: '未視聴',
        statusInProgress: '視聴中',
        statusViewed: '視聴完了',
        colDateEnd: '視聴完了日',
        exportBtn: '視聴者一覧の出力'
    };

    const getStatusConfig = (viewStatus) => {
        const { VIEWED, VIEWING, NOT_VIEWED } = SHARE_KNOWLEDGE_VIEW_STATUS;
        if (viewStatus === VIEWED) {
            return { text: labels.statusViewed, color: SHARE_KNOWLEDGE_VIEW_STATUS_MAPPER[VIEWED].COLOR };
        }
        if (viewStatus === VIEWING) {
            return { text: labels.statusInProgress, color: SHARE_KNOWLEDGE_VIEW_STATUS_MAPPER[VIEWING].COLOR };
        }
        return { text: labels.statusUnviewed, color: SHARE_KNOWLEDGE_VIEW_STATUS_MAPPER[NOT_VIEWED].COLOR };
    };

    return (
        <div id="main" className="wrapper main-student-list main-update">
            <div className="container viewer-list-container">
                <Breadcrumb titlePage="シェアナレッジ" subTitle="視聴者一覧" />
                <div className="share-knowledge-screen">
                    <Link to={SYSTEM_PATH.SHARE_KNOWLEDGE} className="viewer-list-back-link">
                        &lt; 戻る
                    </Link>
                    <div className="uploaded-file-container">
                        <h1 className="title">
                            {labels.title}
                        </h1>
                        <div className="file-info-row">
                            <span className="file-info-label">{labels.fileTitle}</span>
                            <span className="file-info-value">
                                {lstViewer?.fileName || ''}
                            </span>
                        </div>
                    </div>

                    {/* Filter section */}
                    <form onSubmit={handleSearch} className="viewer-list-filter">
                        <div className="filter-item filter-name">
                            <label>{labels.viewerName}</label>
                            <input
                                type="text"
                                placeholder="名前で検索"
                                value={searchName}
                                onChange={(e) => setSearchName(e.target.value)}
                            />
                        </div>
                        <div className="filter-item filter-date">
                            <label>{labels.colDateEnd}</label>
                            <CalendarCustom
                                ranges={searchRanges}
                                displayMode={'dateRange'}
                                onChange={(date) => {
                                    if (Array.isArray(date)) {
                                        setSearchRanges(date);
                                        setSearchFromDate('');
                                        setSearchToDate('');
                                    } else if (date?.selection) {
                                        setSearchRanges([date.selection]);
                                        if (date.selection.startDate && date.selection.endDate) {
                                            setSearchFromDate(moment(date.selection.startDate).format('YYYY-MM-DD'));
                                            setSearchToDate(moment(date.selection.endDate).format('YYYY-MM-DD'));
                                        } else {
                                            setSearchFromDate('');
                                            setSearchToDate('');
                                        }
                                    }
                                }}
                            />
                        </div>
                        <div className="filter-item filter-status">
                            <label>{labels.status}</label>
                            <select
                                value={searchViewStatus}
                                onChange={(e) => setSearchViewStatus(e.target.value)}
                            >
                                <option value="">{labels.statusAll}</option>
                                <option value={SHARE_KNOWLEDGE_VIEW_STATUS.NOT_VIEWED}>{labels.statusUnviewed}</option>
                                <option value={SHARE_KNOWLEDGE_VIEW_STATUS.VIEWING}>{labels.statusInProgress}</option>
                                <option value={SHARE_KNOWLEDGE_VIEW_STATUS.VIEWED}>{labels.statusViewed}</option>
                            </select>
                        </div>
                        <button className="btn-search" type="submit">
                        検索
                        </button>
                    </form>

                    {/* Custom Flex Table matching Figma */}
                    <div className="flex-table-container mg-t-35">
                        <div className="flex-table-selection-header">
                            <SelectPageSize
                                value={pageSize}
                                onChange={(e) => {
                                    setPageSize(Number(e.target.value));
                                    setPage(1);
                                }}
                                values={[10, 50, 100, 500, 1000]}
                            />
                            <div className="flex gap-20 ms-auto align-items-center flex-table-actions">
                                <button
                                    onClick={handleOpenSendMessageModal}
                                    disabled={selectedStudent.length === 0}
                                    className="btn-action-send-message"
                                >
                                    <IconSendMessage />
                                    メッセージ送信
                                </button>
                                <button
                                    onClick={handleExportCSV}
                                    className="btn-action-export"
                                >
                                    <IconExport />
                                    {labels.exportBtn}
                                </button>
                            </div>
                        </div>

                        {/* Table Body */}
                        <div className="flex-table-body">
                            {elements.length > 0 && (
                                <div className="flex-table-row flex-table-header-row">
                                    <div className="flex-table-col col-select">
                                        <span className="col-value">
                                            <input
                                                type="checkbox"
                                                checked={isAllSelected}
                                                onChange={(e) => handleSelectAll(e.target.checked)}
                                                className="flex-table-checkbox"
                                            />
                                        </span>
                                    </div>
                                    <div className="flex-table-col flex-table-header-label">
                                        <span className="text-14 font-bold text-black">全て選択</span>
                                    </div>
                                </div>
                            )}
                            {elements.length > 0 ? (
                                elements.map((el) => {
                                    const isRowSelected = !!selectedRowIds[el.id];
                                    const statusConfig = getStatusConfig(el?.viewStatus);
                                    return (
                                        <div
                                            key={el.id}
                                            className="flex-table-row"
                                        >
                                            <div className="flex-table-col col-select">
                                                <span className="col-label">選択</span>
                                                <span className="col-value">
                                                    <input
                                                        type="checkbox"
                                                        checked={isRowSelected}
                                                        onChange={(e) => handleRowSelect(el, e.target.checked)}
                                                        className="flex-table-checkbox"
                                                    />
                                                </span>
                                            </div>
                                            <div className="flex-table-col col-name">
                                                <span className="col-label">{labels.viewerName}</span>
                                                <span className="col-value name-value">
                                                    {`${el?.firstName || ''} ${el?.lastName || ''}`}
                                                </span>
                                            </div>
                                            <div className="flex-table-col col-email">
                                                <span className="col-label">メールアドレス</span>
                                                <span className="col-value">{el?.email || '--'}</span>
                                            </div>
                                            <div className="flex-table-col col-company">
                                                <span className="col-label">法人名</span>
                                                <span className="col-value">{el?.companyName || '--'}</span>
                                            </div>
                                            {userInfoHeader?.useEmployeeCode && userInfoHeader?.enableDepartmentHierarchy && (
                                                <div className="flex-table-col col-department">
                                                    <span className="col-label">部署名</span>
                                                    <span className="col-value">{el?.departmentName || '--'}</span>
                                                </div>
                                            )}
                                            <div className="flex-table-col col-date-end">
                                                <span className="col-label">{labels.colDateEnd}</span>
                                                <span className="col-value">{formatViewedDate(el?.completedAt)}</span>
                                            </div>
                                            <div className="flex-table-col col-status">
                                                <span className="col-label">{labels.status}</span>
                                                <span
                                                    className="col-value status-badge"
                                                    style={{ color: statusConfig.color }}
                                                >
                                                    {statusConfig.text}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="flex-table-no-data">データはありません</div>
                            )}
                        </div>

                        {/* Table Footer */}
                        {elements.length > 0 && (
                            <div className="flex-table-footer">
                                <PagingInfo tableState={{
                                    pageIndex: page - 1,
                                    pageSize,
                                    totalRecord: initialTableState.totalRecord
                                }} />
                                <ReactPaging
                                    forcePage={page - 1}
                                    pageCount={initialTableState.totalPage}
                                    onPageChange={({ selected }) => {
                                        setPage(selected + 1);
                                    }}
                                />
                            </div>
                        )}
                    </div>

                    {/* Message Modal Component */}
                    <SendMessageScreen
                        selectedStudent={selectedStudent}
                        selectedRowIds={selectedRowIds}
                        setSelectedStudent={setSelectedStudent}
                    />
                </div>
            </div>
        </div>
    );
});

export default ShareKnowledgeViewerListScreen;
